import type { AccountStatus, AccountView, OAuthProviderSlug, Provider } from "@kwp/contracts";
import { OAuthStartOutput } from "@kwp/contracts";
import { z } from "zod";
import { authHeaders, type TokenSource } from "../../lib/api";

/** Settings → Accounts, the pure parts: labels, the connect call and the return messages. */
export const PROVIDER_LABEL: Record<Provider, string> = { gmail: "Google", graph: "Microsoft" };
export const PROVIDER_SLUG: Record<Provider, OAuthProviderSlug> = { gmail: "google", graph: "microsoft" };

export const STATUS: Record<AccountStatus, { label: string; tone: "ok" | "busy" | "bad" }> = {
  connecting: { label: "Connecting", tone: "busy" },
  syncing: { label: "Syncing", tone: "busy" },
  ready: { label: "Ready", tone: "ok" },
  needs_reconnect: { label: "Needs reconnect", tone: "bad" },
  error: { label: "Error", tone: "bad" },
};

export const needsReconnect = (s: AccountStatus) => s === "needs_reconnect" || s === "error";
export const isBusy = (s: AccountStatus) => s === "connecting" || s === "syncing";

/**
 * A ready account syncs at least every 10 minutes (the fallback poll, §6.3), so one that has not
 * for an hour has stopped, whatever its status says.
 */
export const STALE_AFTER_MS = 60 * 60_000;

/**
 * Whether a mailbox is getting its mail (§5): `reconnect` when the provider refused it, `stale`
 * when it says ready but has not synced for `STALE_AFTER_MS`, `ok` otherwise (a first sync in
 * progress included).
 */
export type AccountHealth = "ok" | "reconnect" | "stale";

export function accountHealth(a: Pick<AccountView, "status" | "lastSyncedAt">, now: Date): AccountHealth {
  if (needsReconnect(a.status)) return "reconnect";
  if (a.status === "ready" && a.lastSyncedAt !== null && now.getTime() - a.lastSyncedAt.getTime() > STALE_AFTER_MS) return "stale";
  return "ok";
}

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function syncedAgo(at: Date, now: Date): string {
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

/** What a failed connect means to a person (`?error=` on return, or the start call's reason). */
const CONNECT_ERRORS: Record<string, string> = {
  connect_disabled: "Connecting mail is not configured yet. An admin has to set up the Google and Microsoft sign-in apps first (owner steps E and F).",
  access_denied: "The connection was cancelled at the provider.",
  invalid_callback: "The provider sent back an incomplete answer. Please try again.",
  invalid_state: "The connection link expired or was already used. Please start again.",
  expired_state: "The connection link expired. Please start again.",
  no_refresh_token: "The provider did not grant offline access. Remove KitWork+ from your account's connected apps, then connect again.",
  invalid_grant: "The provider rejected the sign-in. Please try again.",
  workspace: "That workspace does not exist or you are not a member.",
  connect_failed: "The connection failed. Please try again.",
};

export function connectErrorMessage(reason: string): string {
  const known = CONNECT_ERRORS[reason];
  return known === undefined ? `The connection failed (${reason}).` : known;
}

const StartError = z.object({ error: z.object({ code: z.string(), reason: z.string().optional() }) });

export class ConnectError extends Error {
  constructor(readonly reason: string) {
    super(connectErrorMessage(reason));
    this.name = "ConnectError";
  }
}

/**
 * `GET /oauth/{provider}/start?workspaceId=…` with the bearer token (a fetch, so the token never
 * goes in a URL); returns the provider URL to navigate to, or throws a ConnectError.
 */
export async function startConnect(
  fetcher: typeof fetch,
  apiUrl: string,
  getToken: TokenSource,
  provider: OAuthProviderSlug,
  workspaceId: string,
): Promise<string> {
  const url = `${apiUrl.replace(/\/+$/, "")}/oauth/${provider}/start?${new URLSearchParams({ workspaceId }).toString()}`;
  const res = await fetcher(url, { headers: await authHeaders(getToken) });
  const body: unknown = await res.json().catch(() => null);
  if (res.ok) {
    const ok = OAuthStartOutput.safeParse(body);
    if (ok.success) return ok.data.url;
    throw new ConnectError("connect_failed");
  }
  const failed = StartError.safeParse(body);
  if (!failed.success) throw new ConnectError("connect_failed");
  throw new ConnectError(failed.data.error.reason === undefined ? failed.data.error.code : failed.data.error.reason);
}
